<script lang="ts" setup>
import type { PropType } from 'vue'

import OverlayBase from '@/components/overlays/OverlayBase.vue'
import ResponsiveImage from '@/components/ResponsiveImage.vue'
import TwoColsBlock from '@/components/layout/TwoColsBlock.vue'
import SkrHeading from '@/components/SkrHeading.vue'
import SkrButtonGroup from '@/components/button/SkrButtonGroup.vue'
import SkrButton from '@/components/button/SkrButton.vue'

interface ResponsiveImage {
  source: string
  width: number
  height: number
}

interface OverlayContent {
  title: string
  subtitle?: string
  action?: {
    text: string
    url?: string
  }
  image: ResponsiveImage
  isAtrustBatch?: boolean
}
const props = defineProps({
  message: String,
  status: String,
  processedRequests: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
})

const emit = defineEmits<{
  (e: 'action', type: OverlayAction, data?: unknown): void
  (e: 'close'): void
}>()

const { t } = useI18n()

const { areExitUrlsApplicable, leaveToErrorUrl } = useExitUrlStore()

const isCustomExitUrl = computed(() => areExitUrlsApplicable(props.processedRequests))

const signingStatus = computed(() => props.status)
const signingMessage = computed(() => props.message)
const isSingleDocument = computed(() => props.processedRequests.length === 1)

const returnMessage = computed(() => {
  return isSingleDocument.value ? t('sign.error.one.back') : t('sign.error.many.back')
})
const isTimeoutMessage = computed(() => {
  return ['Timeout', 'Session', 'Tempo', 'Zeit'].some(t => props.message?.includes(t))
})

const data = computed<OverlayContent>(() => {
  switch (signingStatus.value) {
    case 'cancelled':
      return {
        title: t('sign.error.cancelled.title'),
        subtitle: t('sign.error.cancelled.subtitle'),
        image: { source: 'sign-cancel', width: 565, height: 414 },
      }
    case 'timeout':
      return {
        title: t('sign.error.timeout.title'),
        subtitle: t('sign.error.timeout.subtitle'),
        image: { source: 'verification-code-expired', width: 560, height: 510 },
      }
  }

  // signingStatus error, for frequent error cases we show dedicated screens
  // based on the Swisscom AIS SubsystemError message
  if (signingMessage.value?.includes('https://mobileid.ch/activate')) {
    return {
      title: t('sign.error.title'),
      subtitle: t('sign.error.mobile_id.not_active'),
      action: {
        text: t('sign.error.mobile_id.link_text'),
        url: t('sign.error.mobile_id.link_url'),
      },
      image: { source: 'identity-error', width: 416, height: 410 },
    }
  } else if (signingMessage.value?.includes('PIN')) {
    return {
      title: t('sign.error.title'),
      subtitle: t('sign.error.mobile_id.pin_blocked'),
      action: {
        text: t('sign.error.mobile_id.link_text'),
        url: t('sign.error.mobile_id.link_url'),
      },
      image: { source: 'identity-error', width: 416, height: 410 },
    }
  } else if (isTimeoutMessage.value) {
    return {
      title: t('sign.error.title'),
      subtitle: t('sign.error.mobile_id.timeout'),
      image: { source: 'tan-expired', width: 560, height: 511 },
    }
  } else if (signingMessage.value?.includes('status:InvalidPassword')) {
    return {
      title: t('pwd_otp.pwd.error.title'),
      subtitle: t('pwd_otp.pwd.error.text'),

      action: {
        text: t('pwd_otp.pwd.error.cta'),
      },
      image: { source: 'incorrect-password', width: 516, height: 441 },
    }
  } else if (signingMessage.value?.includes('status:InvalidOtp')) {
    return {
      title: t('pwd_otp.otp.error.title'),
      subtitle: t('pwd_otp.otp.error.text'),
      action: {
        text: t('pwd_otp.otp.error.cta'),
      },
      image: { source: 'verification-code-wrong', width: 688, height: 426 },
    }
  } else if (signingMessage.value?.includes('no_free_signatures_remaining')) {
    return {
      title: t('sign.error.no_free_signatures.title'),
      subtitle: t('sign.error.no_free_signatures.subtitle'),
      action: {
        text: t('sign.error.no_free_signatures.cta'),
      },
      image: { source: 'error-no-free-sigs', width: 560, height: 406 },
    }
  } else if (signingMessage.value?.includes('Batch signature not supported by AT')) {
    return {
      title: t('sign.error.no_batch_at.title'),
      image: { source: 'sign-error', width: 554, height: 352 },
      isAtrustBatch: true,
    }
  } else {
    return {
      title: t('sign.error.title'),
      subtitle: t('sign.error.general_error'),
      image: { source: 'sign-error', width: 554, height: 352 },
    }
  }
})
</script>

<template>
  <overlay-base :transition="false">
    <two-cols-block split="5/7" class="fill-height">
      <template #left>
        <div class="mb-8">
          <skr-heading level="1" data-cy="title" semantic>{{ data.title }}</skr-heading>
          <skr-heading class="mb-6" level="2">
            <i18n-t v-if="data.isAtrustBatch" keypath="sign.error.no_batch_at.subtitle">
              <template #link>
                <a
                  :href="t('sign.error.help.link_url')"
                  target="_blank"
                  rel="noopener"
                  data-cy="signing_error_subtitle_link"
                  >{{ t('sign.error.no_batch_at.link_text') }}</a
                >
              </template>
            </i18n-t>
            <template v-else>{{ data.subtitle }}</template>
          </skr-heading>
        </div>
        <skr-button-group size="lg" stacked>
          <skr-button
            v-if="data.action?.url"
            data-cy="signing_error_action_button"
            :href="data.action.url"
            target="_blank"
            rel="noopener"
          >
            {{ data.action.text }}
          </skr-button>

          <skr-button
            data-cy="signing_error_back_button"
            :type="data.action?.url ? 'text' : 'primary'"
            @click="emit('close')"
          >
            {{ data.action && !data.action.url ? data.action.text : returnMessage }}
          </skr-button>

          <skr-button v-if="isCustomExitUrl" type="text" @click="leaveToErrorUrl">
            {{ t('global.cancel') }}
          </skr-button>
        </skr-button-group>
        <div v-if="!data.isAtrustBatch" class="pt-12" data-cy="signing_error_help">
          <p>
            <i18n-t keypath="sign.error.help.text">
              <template #question>
                <strong>{{ t('sign.error.help.question') }}</strong>
              </template>
              <template #contactText>
                <a :href="t('sign.error.help.link_url')" target="_blank" rel="noopener">{{
                  t('sign.error.help.link_text')
                }}</a>
              </template>
            </i18n-t>
          </p>
        </div>
      </template>
      <template #right>
        <responsive-image v-bind="data.image" />
      </template>
    </two-cols-block>
  </overlay-base>
</template>
